import React, { useContext, useEffect, useMemo, useState } from 'react';
import Box from '@amzn/meridian/box';
import Row from '@amzn/meridian/row';
import Responsive from '@amzn/meridian/responsive';
import DataSource from '../../api';
import DataState from '../../Types/DataState';
import { StateContext } from '../../GlobalStateStore';
import { EmptyTeamProfile, Profile, Stats, TeamProfile } from '../../Types';
import { weeksList } from '../../Util';
import ProfileContainer from '../Dashboard/DashboardContainers/ProfileContainer';
import {
  DISPLAY,
  NOTDISPLAY,
} from '../Dashboard/DashboardComponents/LighthouseMetrics/helper';
import {
  GetBatchCoachingStatsResponse,
  MonthlyPeriod,
} from '@amzn/aet-coachingstats-kit';
import Card from '@amzn/meridian/card/card';
import Column from '@amzn/meridian/column/column';
import Heading from '@amzn/meridian/heading/heading';
import Alert from '@amzn/meridian/alert/alert';
import ButtonGroup, { ButtonOption } from '@amzn/meridian/button-group';
import Loader from '@amzn/meridian/loader/loader';
import OMLevelCoachingCompletionRates from './OMLevelCoachingCompletionRates';
import { FormattedMessage } from 'react-intl';
import {
  buildCoachingsTableProps,
  buildContactReviewsTableProps,
  buildUserInfoFromDirectReportAlias,
  getTMDirectReportsOfOM,
  getTMDirectReportsOfOMMap,
  isLastMonthDataAvailable,
} from './OmCoachingHelper';

import {
  getCoachingStatsSummaryForTm,
  getOMCoachingStatsSummary,
  getOMPeriodsForNow,
} from '../../Util/CoachingStatsUtilsForOM';
import { TMCoachingStatsSummaryForOM } from '../../Types/CoachingStats';

import { MonthSelection, TmTabSelection } from './types';
import TmTable from './TmTable';
import TMLevelCoachingCompletionRates from './TMLevelCoachingCompletionRates';
import { ReviewTypes } from '../../api/Naws';
import ContactReviewsTable from './ContactReviewsTable';
import CoachingsTable from './CoachingsTable';
import { getRequesterRole } from './helper';
import useCustomTracking from '../../Util/useCustomTracking';
import _ from 'lodash';

interface OmCoachingProps {
  loggedInProfile: Profile;
  displayedProfile: Profile;
  loggedInProfileState: string;
  displayedProfileState: string;
}

const TRAILING_DAYS = 'Trailing 7 days';

const OmCoaching: React.FC<OmCoachingProps> = ({
  loggedInProfile,
  displayedProfile,
  loggedInProfileState,
  displayedProfileState,
}: OmCoachingProps): React.ReactElement => {
  const apiNaws = DataSource.getDataFeed('NAWS');
  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const {
    userID,
    cscEntityPrefix,
    isTm,
    isOm,
    requesterUserID,
    requesterUsername,
  } = inspireOptions.data;
  const [teamProfile, setTeamProfile] = useState<TeamProfile>(EmptyTeamProfile);
  const [teamProfileState, setTeamProfileState] = useState(DataState.LOADING);
  const [coachingsAndContactReviews, setCoachingsAndContactReviews] =
    useState<Stats>();
  const [coachingsAndContactReviewsState, setCoachingsAndContactReviewsState] =
    useState(DataState.LOADING);
  const [tmTabSelected, setTmTabSelected] = useState(TmTabSelection.COACHINGS);

  const [selectedTmUserId, setSelectedTmUserId] = useState<string>();

  const weekOptions = [TRAILING_DAYS, ...weeksList()];
  const [weekValue, setWeekValue] = useState(weekOptions[0]);

  const setWeek = (e: React.FormEvent<HTMLSelectElement>): void => {
    setWeekValue(e.toString());
  };
  const [monthSelection, setMonthSelection] = useState(
    MonthSelection.THIS_MONTH
  );

  useEffect(() => {
    if (!userID || !cscEntityPrefix) return;
    const getTeamProfileDataQuery = apiNaws.prepareTeamProfileDataQuery(
      userID,
      cscEntityPrefix
    );
    apiNaws.getTeamProfileData(
      getTeamProfileDataQuery,
      setTeamProfile,
      setTeamProfileState,
      'cache-first'
    );
  }, [userID, cscEntityPrefix]);

  const requestorRole = getRequesterRole(isOm, isTm);

  const thisMonthButtonClickStreamContentId =
    'om-coaching-page-view-by-month-this-month-button';
  const lastMonthButtonClickStreamContentId =
    'om-coaching-page-view-by-month-last-month-button';

  const periods = useMemo(
    () => getOMPeriodsForNow(monthSelection),
    [monthSelection]
  );

  // Assuming getOMPeriodsForNow will keep returning monthly period as the first item.
  const selectedMonthPeriod = periods[0] as MonthlyPeriod;

  const [
    SelectedMonthButtonClickStreamTrackingTag, // Using TitleCase naming convention as we will use the state as component name below.
    emitSelectedMonthClickstreamTags,
  ] = useCustomTracking(
    thisMonthButtonClickStreamContentId,
    'om-coaching-page-view-by-month-slot',
    {
      'data-csa-c-type': 'tab',
      'data-csa-c-tag1': 'OM Coaching',
      'data-csa-c-tag2': `${selectedMonthPeriod.year}-${selectedMonthPeriod.month}`,
      'data-csa-c-profile-id': requesterUserID,
      'data-csa-c-username': requesterUsername,
      'data-csa-c-requester-role': requestorRole,
    }
  );

  const handleMonthSelectionChange = (newMonthSelection: MonthSelection) => {
    setMonthSelection(newMonthSelection);
  };

  useEffect(() => {
    if (!selectedMonthPeriod) {
      return;
    }

    emitSelectedMonthClickstreamTags();
  }, [selectedMonthPeriod]);

  const [batchCoachingStats, setBatchCoachingStats] =
    useState<GetBatchCoachingStatsResponse>();
  const [batchCoachingStatsState, setBatchCoachingStatsState] = useState(
    DataState.LOADING
  );

  const tmDirectReportsOfOM = useMemo(() => {
    if (teamProfile) {
      return getTMDirectReportsOfOM(teamProfile);
    }
  }, [teamProfile]);

  const tmDirectReportsOfOMMap = useMemo(() => {
    if (tmDirectReportsOfOM && cscEntityPrefix) {
      return getTMDirectReportsOfOMMap(tmDirectReportsOfOM, cscEntityPrefix);
    }
  }, [tmDirectReportsOfOM, cscEntityPrefix]);

  useEffect(() => {
    if (
      teamProfileState !== DataState.LOADED ||
      !teamProfile ||
      !periods ||
      !tmDirectReportsOfOM ||
      !cscEntityPrefix ||
      teamProfile === EmptyTeamProfile
    ) {
      return;
    }

    const queryBatchCoachingStats =
      apiNaws.prepareQueryForBatchGetCoachingStats(
        tmDirectReportsOfOM,
        periods,
        cscEntityPrefix
      );

    apiNaws.getBatchCoachingStats(
      queryBatchCoachingStats,
      setBatchCoachingStats,
      setBatchCoachingStatsState,
      'cache-first'
    );
  }, [
    teamProfileState,
    teamProfile,
    periods,
    monthSelection,
    tmDirectReportsOfOM,
    cscEntityPrefix,
    apiNaws,
  ]);

  const omCoachingStatsSummary = useMemo(() => {
    if (
      periods &&
      batchCoachingStats &&
      batchCoachingStats.results &&
      tmDirectReportsOfOMMap
    ) {
      return getOMCoachingStatsSummary(
        periods,
        batchCoachingStats.results,
        tmDirectReportsOfOMMap
      );
    }
  }, [batchCoachingStats, periods, tmDirectReportsOfOMMap]);

  const tmLevelStats = omCoachingStatsSummary?.tmStats;
  const omLevelStats = omCoachingStatsSummary?.omStats;

  const selectedTmCoachingStatsSummary = useMemo(() => {
    if (!omCoachingStatsSummary?.tmStats) return undefined;

    // If user has selected a specific TM, find it in current month's data
    if (selectedTmUserId) {
      return getCoachingStatsSummaryForTm(
        omCoachingStatsSummary,
        selectedTmUserId
      );
    }

    // Fallback to first TM (preselected)
    return omCoachingStatsSummary.tmStats[0];
  }, [omCoachingStatsSummary, selectedTmUserId]);

  useEffect(() => {
    if (!tmDirectReportsOfOM || !selectedTmCoachingStatsSummary) return;
    const directReportsOfSelectedTm =
      selectedTmCoachingStatsSummary.tmInfo.subReports;

    if (monthSelection && directReportsOfSelectedTm.length) {
      const queryStats = apiNaws.prepareQueryforGetCoachingsAndContactReviews(
        monthSelection,
        cscEntityPrefix,
        [ReviewTypes.COACHING_NOTES, ReviewTypes.CONTACT_REVIEW],
        directReportsOfSelectedTm.map(directReport =>
          buildUserInfoFromDirectReportAlias(directReport)
        )
      );
      apiNaws.getCoachingsAndContactReviews(
        queryStats,
        setCoachingsAndContactReviews,
        setCoachingsAndContactReviewsState,
        'cache-first'
      );
    }
  }, [
    monthSelection,
    selectedTmCoachingStatsSummary,
    cscEntityPrefix,
    tmDirectReportsOfOM,
    apiNaws,
  ]);

  const isLoading =
    teamProfileState === DataState.LOADING ||
    batchCoachingStatsState === DataState.LOADING ||
    !!omLevelStats == false || // is omLevelStats falsy
    !!tmLevelStats == false; // is tmLevelStats falsy

  const isLoaded =
    teamProfileState === DataState.LOADED &&
    batchCoachingStatsState === DataState.LOADED &&
    omLevelStats &&
    tmLevelStats;

  return (
    <Box>
      <Row
        width='100%'
        widths={['fill', 'fit']}
        alignmentVertical='top'
        height='100%'
      >
        <Responsive
          query='min-width'
          props={{
            isBig: {
              default: false,
              '1080px': true,
            },
            subMetricsTitleDisplay: {
              '512px': DISPLAY,
              '628px': NOTDISPLAY,
              '985px': DISPLAY,
              '1210px': NOTDISPLAY,
              '1440px': DISPLAY,
            },
          }}
        >
          {({ isBig }: any): React.ReactElement => (
            <>
              <Box height='100%'>
                <ProfileContainer
                  state={displayedProfileState}
                  profile={displayedProfile}
                  selectedWeek={weekValue}
                  setWeek={setWeek}
                  isBig={isBig}
                />
                {
                  <Row widths={['50%', '50%']} alignmentVertical='stretch'>
                    <Card width={'50%'} spacingInset='300' min-height='900px'>
                      <Row widths={['60%', '40%']} spacingInset='300'>
                        <Column width='60%'>
                          <Heading level={2} type='h400'>
                            <FormattedMessage
                              id='cs_perfportal_coaching_page_heading_om_coaching_completion_rates'
                              defaultMessage='TM Coaching completion rates'
                            />
                          </Heading>
                        </Column>
                        <Column width='40%'>
                          <SelectedMonthButtonClickStreamTrackingTag />

                          <ButtonGroup
                            value={monthSelection}
                            onChange={handleMonthSelectionChange}
                          >
                            <ButtonOption
                              value={MonthSelection.THIS_MONTH}
                              disabled={false}
                            >
                              <FormattedMessage
                                id='cs_perfportal_coaching_page_heading_om_coaching_completion_rates_this_month'
                                defaultMessage='This Month'
                              />
                            </ButtonOption>
                            <ButtonOption
                              value={MonthSelection.LAST_MONTH}
                              disabled={false}
                            >
                              <FormattedMessage
                                id='cs_perfportal_coaching_page_heading_om_coaching_completion_rates_last_month'
                                defaultMessage='Last Month'
                              />
                            </ButtonOption>
                          </ButtonGroup>
                        </Column>
                      </Row>
                      {isLoading && (
                        <Row
                          type='outline'
                          width='100%'
                          alignmentHorizontal='center'
                          height='900px'
                        >
                          <Loader />
                        </Row>
                      )}
                      {teamProfileState === DataState.ERROR && (
                        <Box height='150px' width='fill' spacingInset='300'>
                          <Alert type='error'>
                            <FormattedMessage
                              id='cs_perfportal_error_refresh_page'
                              defaultMessage='Something went wrong. Please try refreshing the page. 
                If the problem persists, please submit a SIM ticket for us to troubleshoot.'
                            />
                          </Alert>
                        </Box>
                      )}
                      {isLoaded && selectedTmCoachingStatsSummary && (
                        <>
                          {/* TODO: Remove the section below after May 2025. */}
                          {isLastMonthDataAvailable(monthSelection) ? (
                            <Box height='100px' width='fill' spacingInset='300'>
                              <Alert type='neutral'>
                                <FormattedMessage
                                  id='cs_perfportal_no_aggregated_data_before_may'
                                  defaultMessage='Aggregated coaching stats are only available starting May 2025.'
                                />
                              </Alert>
                            </Box>
                          ) : (
                            <Column alignmentVertical='top' height={'220px'}>
                              <OMLevelCoachingCompletionRates
                                monthlyCoachingCompletionPercentage={
                                  omLevelStats.monthlyCoachingCompletionPercentage
                                }
                                weeklyCoachingCompletionPercentages={
                                  omLevelStats.weeklyCoachingCompletionPercentages
                                }
                              />
                            </Column>
                          )}

                          <Column
                            spacingInset='200 none'
                            alignmentVertical='bottom'
                          >
                            <TmTable
                              tmTableRowContentPropsList={tmLevelStats}
                              batchCoachingStatsState={batchCoachingStatsState}
                              selectedTmCoachingStatsSummary={
                                selectedTmCoachingStatsSummary
                              }
                              setSelectedTmUserId={setSelectedTmUserId}
                            />
                          </Column>
                        </>
                      )}
                    </Card>
                    <Card width='50%' spacingInset='300'>
                      <Column alignmentVertical='top' height={'310px'}>
                        {selectedTmCoachingStatsSummary && (
                          <TMLevelCoachingCompletionRates
                            tmCoachingStatsSummary={
                              selectedTmCoachingStatsSummary
                            }
                            batchCoachingStatsState={batchCoachingStatsState}
                            monthSelection={monthSelection}
                            tmTabSelected={tmTabSelected}
                            setTmTabSelected={setTmTabSelected}
                            coachingsAndContactReviews={
                              coachingsAndContactReviews
                            }
                            coachingsAndContactReviewsState={
                              coachingsAndContactReviewsState
                            }
                          />
                        )}
                      </Column>
                      <Column
                        spacingInset='200 none'
                        alignmentVertical='bottom'
                      >
                        {tmTabSelected === TmTabSelection.CONTACT_REVIEWS ? (
                          <ContactReviewsTable
                            contactReviews={buildContactReviewsTableProps(
                              coachingsAndContactReviews,
                              monthSelection
                            )}
                            statsState={coachingsAndContactReviewsState}
                            monthSelection={monthSelection}
                          />
                        ) : (
                          <CoachingsTable
                            coachings={buildCoachingsTableProps(
                              coachingsAndContactReviews,
                              monthSelection
                            )}
                            statsState={coachingsAndContactReviewsState}
                          />
                        )}
                      </Column>
                    </Card>
                  </Row>
                }
              </Box>
            </>
          )}
        </Responsive>
      </Row>
    </Box>
  );
};

export default OmCoaching;
